/**
 * features/tax-invoices/TaxInvoicesListPage.tsx
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Receipt, Search } from 'lucide-react';
import {
  TAX_INVOICE_STATUS_LABELS,
  TAX_INVOICE_STATUSES,
  formatDate,
  formatMoney,
  type TaxInvoiceStatus,
} from '@ft/core';
import { useDebounced } from '~/lib/use-debounced';
import { PageHeader, Pagination, Toolbar } from '~/ui/layout';
import { Button, Card, EmptyState, Input, Select, StatusBadge, TableSkeleton } from '~/ui/primitives';
import { useTaxInvoices } from './hooks';

const PAGE_SIZE = 15;

export function TaxInvoicesListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<TaxInvoiceStatus | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const search = useDebounced(searchInput);

  const { data, isLoading, isFetching } = useTaxInvoices({
    page,
    pageSize: PAGE_SIZE,
    search,
    status: status || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  return (
    <div>
      <PageHeader
        title="ใบเสร็จ/ใบกำกับภาษี"
        description="เอกสารที่ออกให้ลูกค้าเมื่อได้รับชำระเงิน"
        actions={
          <Button variant="primary" onClick={() => navigate('/tax-invoices/new')}>
            <Plus size={16} />
            สร้างใบกำกับภาษี
          </Button>
        }
      />

      <Card>
        <Toolbar className="flex-wrap gap-2">
          <div className="relative w-full max-w-xs">
            <Search size={15} className="absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-subtle" aria-hidden />
            <Input
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                setPage(1);
              }}
              placeholder="ค้นหาเลขที่เอกสาร, ชื่อลูกค้า"
              className="pl-8"
            />
          </div>

          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as TaxInvoiceStatus | '');
              setPage(1);
            }}
            className="w-40"
          >
            <option value="">ทุกสถานะ</option>
            {TAX_INVOICE_STATUSES.map((value) => (
              <option key={value} value={value}>
                {TAX_INVOICE_STATUS_LABELS[value]}
              </option>
            ))}
          </Select>

          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(1);
            }}
            className="w-40"
            aria-label="จากวันที่"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(1);
            }}
            className="w-40"
            aria-label="ถึงวันที่"
          />

          {isFetching && !isLoading ? <span className="text-xs text-fg-subtle">กำลังอัปเดต…</span> : null}
        </Toolbar>

        {isLoading ? (
          <TableSkeleton columns={5} />
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            icon={<Receipt size={28} />}
            title={search || status ? 'ไม่พบเอกสารที่ค้นหา' : 'ยังไม่มีใบเสร็จ/ใบกำกับภาษี'}
            description={search || status ? undefined : 'ออกได้จากใบเสนอราคา หรือสร้างใหม่ที่นี่'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>เลขที่เอกสาร</th>
                    <th>ลูกค้า</th>
                    <th>วันที่</th>
                    <th className="text-right">ยอดรวม</th>
                    <th>สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/tax-invoices/${invoice.id}`)}
                    >
                      <td className="font-medium text-fg">{invoice.documentNumber}</td>
                      <td className="text-fg-muted">{invoice.customerName}</td>
                      <td className="tabular text-fg-muted">{formatDate(invoice.date)}</td>
                      <td className="tabular text-right text-fg">{formatMoney(invoice.totalAmount)}</td>
                      <td>
                        <StatusBadge status={invoice.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              pageSize={data.pageSize}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}
